/**
 * Legal Settings Tab Component
 *
 * Allows admins to manage Terms & Conditions and Privacy Policy content,
 * and force re-acceptance when content changes.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Badge } from '../../ui/badge';
import { FileText, Shield, Save, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { platformSettingsAPI } from '../../../lib/api';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import RichTextEditor from '../../common/RichTextEditor';
import { Alert, AlertTitle, AlertDescription } from '../../ui/alert';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { DocumentAcknowledgementCard } from './DocumentAcknowledgementCard';

interface LegalState {
  termsContent: string;
  privacyContent: string;
  termsVersion: number;
  privacyVersion: number;
  termsUpdatedAt: string | null;
  privacyUpdatedAt: string | null;
}

export function LegalTab() {
  const [legal, setLegal] = useState<LegalState>({
    termsContent: '',
    privacyContent: '',
    termsVersion: 1,
    privacyVersion: 1,
    termsUpdatedAt: null,
    privacyUpdatedAt: null,
  });
  const [requireTermsReaccept, setRequireTermsReaccept] = useState(false);
  const [requirePrivacyReaccept, setRequirePrivacyReaccept] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingTerms, setSavingTerms] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  // Persist the backend's failure reason inline (not toast-only) so the user
  // can see *why* a save failed — e.g. a permission (403) or context (400)
  // error — instead of a generic "Failed to save" with no cause.
  const [termsError, setTermsError] = useState<string | null>(null);
  const [privacyError, setPrivacyError] = useState<string | null>(null);

  const loadLegalSettings = useCallback(async () => {
    try {
      const response = await platformSettingsAPI.getSettings();
      const settings = response.data;
      setLegal({
        termsContent: settings.terms_content || '',
        privacyContent: settings.privacy_content || '',
        termsVersion: settings.terms_version ?? 1,
        privacyVersion: settings.privacy_version ?? 1,
        termsUpdatedAt: settings.terms_updated_at || null,
        privacyUpdatedAt: settings.privacy_updated_at || null,
      });
    } catch (error) {
      console.error('Failed to load legal settings:', error);
      toast.error('Failed to load legal settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLegalSettings();
  }, [loadLegalSettings]);

  const saveTerms = useCallback(async () => {
    setSavingTerms(true);
    setTermsError(null);
    try {
      const payload: Record<string, unknown> = {
        terms_content: legal.termsContent,
        terms_updated_at: new Date().toISOString(),
      };

      // If "require re-acceptance" is checked, increment the version
      if (requireTermsReaccept) {
        payload['terms_version'] = legal.termsVersion + 1;
      }

      await platformSettingsAPI.patchSettings(payload as Record<string, string>);

      // Update local state
      if (requireTermsReaccept) {
        setLegal(prev => ({
          ...prev,
          termsVersion: prev.termsVersion + 1,
          termsUpdatedAt: new Date().toISOString(),
        }));
        setRequireTermsReaccept(false);
      } else {
        setLegal(prev => ({
          ...prev,
          termsUpdatedAt: new Date().toISOString(),
        }));
      }

      toast.success('Terms & Conditions saved successfully', {
        description: requireTermsReaccept
          ? 'All users will be required to re-accept on next login.'
          : 'Content updated without requiring re-acceptance.',
      });
    } catch (error) {
      console.error('Failed to save terms:', error);
      const message = getApiErrorMessage(error, 'Failed to save Terms & Conditions');
      setTermsError(message);
      toast.error(message);
    } finally {
      setSavingTerms(false);
    }
  }, [legal.termsContent, legal.termsVersion, requireTermsReaccept]);

  const savePrivacy = useCallback(async () => {
    setSavingPrivacy(true);
    setPrivacyError(null);
    try {
      const payload: Record<string, unknown> = {
        privacy_content: legal.privacyContent,
        privacy_updated_at: new Date().toISOString(),
      };

      // If "require re-acceptance" is checked, increment the version
      if (requirePrivacyReaccept) {
        payload['privacy_version'] = legal.privacyVersion + 1;
      }

      await platformSettingsAPI.patchSettings(payload as Record<string, string>);

      // Update local state
      if (requirePrivacyReaccept) {
        setLegal(prev => ({
          ...prev,
          privacyVersion: prev.privacyVersion + 1,
          privacyUpdatedAt: new Date().toISOString(),
        }));
        setRequirePrivacyReaccept(false);
      } else {
        setLegal(prev => ({
          ...prev,
          privacyUpdatedAt: new Date().toISOString(),
        }));
      }

      toast.success('Privacy Policy saved successfully', {
        description: requirePrivacyReaccept
          ? 'All users will be required to re-accept on next login.'
          : 'Content updated without requiring re-acceptance.',
      });
    } catch (error) {
      console.error('Failed to save privacy:', error);
      const message = getApiErrorMessage(error, 'Failed to save Privacy Policy');
      setPrivacyError(message);
      toast.error(message);
    } finally {
      setSavingPrivacy(false);
    }
  }, [legal.privacyContent, legal.privacyVersion, requirePrivacyReaccept]);

  // CR-6307: delegate to the preference-aware formatter, keeping the fallback.
  const { formatDateTime: fmtDT } = useFormatters();
  const formatDate = (dateStr: string | null) => (dateStr ? fmtDT(dateStr) : 'Never');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Terms & Conditions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Terms & Conditions
          </CardTitle>
          <CardDescription>
            Manage the Terms & Conditions content displayed on the /terms page.
            Users can be required to accept updated terms on next login.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Version & Last Updated */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Version:</span>
              <Badge variant="secondary">v{legal.termsVersion}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Last updated:</span>
              <span>{formatDate(legal.termsUpdatedAt)}</span>
            </div>
          </div>

          {/* Rich Text Editor */}
          <div className="space-y-2">
            <Label>Content</Label>
            <RichTextEditor
              value={legal.termsContent}
              onChange={(html) => setLegal(prev => ({ ...prev, termsContent: html }))}
              placeholder="Enter your Terms & Conditions content here..."
            />
          </div>

          {/* Require Re-acceptance Toggle */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                <Label className="font-medium">Require re-acceptance</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                When enabled, saving will increment the version and all users must accept the updated terms on next login.
              </p>
            </div>
            <Switch
              checked={requireTermsReaccept}
              onCheckedChange={setRequireTermsReaccept}
            />
          </div>

          {/* Inline failure reason — persists so the cause stays visible */}
          {termsError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Could not save Terms &amp; Conditions</AlertTitle>
              <AlertDescription>{termsError}</AlertDescription>
            </Alert>
          )}

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={saveTerms} disabled={savingTerms}>
              {savingTerms ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Terms & Conditions
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Privacy Policy */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Privacy Policy
          </CardTitle>
          <CardDescription>
            Manage the Privacy Policy content displayed on the /privacy page.
            Users can be required to accept updated policy on next login.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Version & Last Updated */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Version:</span>
              <Badge variant="secondary">v{legal.privacyVersion}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Last updated:</span>
              <span>{formatDate(legal.privacyUpdatedAt)}</span>
            </div>
          </div>

          {/* Rich Text Editor */}
          <div className="space-y-2">
            <Label>Content</Label>
            <RichTextEditor
              value={legal.privacyContent}
              onChange={(html) => setLegal(prev => ({ ...prev, privacyContent: html }))}
              placeholder="Enter your Privacy Policy content here..."
            />
          </div>

          {/* Require Re-acceptance Toggle */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                <Label className="font-medium">Require re-acceptance</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                When enabled, saving will increment the version and all users must accept the updated policy on next login.
              </p>
            </div>
            <Switch
              checked={requirePrivacyReaccept}
              onCheckedChange={setRequirePrivacyReaccept}
            />
          </div>

          {/* Inline failure reason — persists so the cause stays visible */}
          {privacyError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Could not save Privacy Policy</AlertTitle>
              <AlertDescription>{privacyError}</AlertDescription>
            </Alert>
          )}

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={savePrivacy} disabled={savingPrivacy}>
              {savingPrivacy ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Privacy Policy
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Document acknowledgement wording (Read & Accept / Read & Understood) */}
      <DocumentAcknowledgementCard />
    </div>
  );
}
